import { useState, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Network, ChevronRight, Building2 } from 'lucide-react';
import ContainerLayout from '@/shared/components/ContainerLayout';
import EmptyView from '@/shared/components/EmptyView';
import L2VPNCircuitCard from '@/domains/l2vpns/components/L2VPNCircuitCard';
import AddL2VPNCircuitToAccountDialog from '@/domains/l2vpns/dialogs/AddL2VPNCircuitToAccountDialog';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import SearchBar from '@/shared/components/SearchBar';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { useUser } from '@/contexts/UserContext';
import { useInfiniteL2VPNCircuits } from '@/domains/l2vpns/services/l2vpn-circuits';
import { useRemoveL2VPNCircuitRecordFromOrganization } from '@/domains/accounts/services/accounts';
import { AccountL2VPNCircuit, L2VPNCircuit } from '@/shared/types/l2vpnTypes';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { AccountDetailsTab } from '@/shared/config/account-details-tabs';
import { Organization } from '@/shared/types/userTypes';
import { useRouteNavigation } from '@/shared/hooks/use-route-navigation';
import { Separator } from '@/shared/ui/separator';
import { OrgType } from '@/shared/types/userRoleTypes';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { usePermissions } from '@/shared/hooks/use-permissions';

export const L2VPNCircuitsPage = () => {
  const { selectedRegionCode } = useUser();
  const { switchOrganizationAndNavigateToAccountDetails } = useRouteNavigation();

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(OrgType.PLATFORM, Feature.L2VPNS, Permission.MANAGE);

  const queryClient = useQueryClient();
  const {
    data: circuitsInfiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    refetch: refetchL2VPNCircuits,
  } = useInfiniteL2VPNCircuits(selectedRegionCode);
  const removeL2VPNCircuitRecordFromOrganization = useRemoveL2VPNCircuitRecordFromOrganization();

  // State for dialogs
  const [isAddL2VPNCircuitToOrganizationDialogOpen, setIsAddL2VPNCircuitToOrganizationDialogOpen] = useState(false);
  const [isRemoveL2VPNCircuitFromOrganizationDialogOpen, setIsRemoveL2VPNCircuitFromOrganizationDialogOpen] = useState(false);
  const pendingRemove = useRef<{ circuitRecordId: string; organizationId: string } | null>(null);

  const [searchTerm, setSearchTerm] = useState('');

  const accountL2VPNCircuits = useMemo(() => {
    const pages = circuitsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: AccountL2VPNCircuit[] = [];
    for (const page of pages) {
      for (const c of page.results ?? []) {
        if (!seen.has(c.organizationId)) {
          seen.add(c.organizationId);
          merged.push(c);
        }
      }
    }
    return merged;
  }, [circuitsInfiniteData?.pages]);

  const totalCount = circuitsInfiniteData?.pages[0]?.totalCount ?? 0;

  const filteredL2VPNCircuits = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return accountL2VPNCircuits?.filter(accountL2VPNCircuit => {
      return (
        accountL2VPNCircuit.name?.toLowerCase().includes(searchLower) ||
        accountL2VPNCircuit.organizationId?.toLowerCase().includes(searchLower) ||
        accountL2VPNCircuit.circuits?.some(l2vpnCircuit => l2vpnCircuit.circuitId?.toLowerCase().includes(searchLower)) ||
        accountL2VPNCircuit.circuits?.some(l2vpnCircuit => l2vpnCircuit.records?.some(record => record.circuitName?.toLowerCase().includes(searchLower)))
      );
    });
  }, [accountL2VPNCircuits, searchTerm]);

  // Group L2VPN circuits by account ID
  const groupedL2VPNCircuits = useMemo(() => {
    if (!filteredL2VPNCircuits) return {};

    const groups: Record<string, L2VPNCircuit[]> = {};

    filteredL2VPNCircuits.forEach(accountL2VPNCircuit => {
      const groupKey = accountL2VPNCircuit.organizationId;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(...accountL2VPNCircuit.circuits);
    });

    return groups;
  }, [filteredL2VPNCircuits]);


  const refetchAccountL2VPNCircuitsData = useCallback(() => {
    if (selectedRegionCode) {
      void queryClient.resetQueries({ queryKey: ['region-l2vpn-circuits-infinite', selectedRegionCode] });
    }
  }, [queryClient, selectedRegionCode]);

  const onManageOrganization = async (organization: Organization) => {
    switchOrganizationAndNavigateToAccountDetails({ organization, accountDetailsTab: AccountDetailsTab.L2VPN_CIRCUITS });
  };

  const handleRemoveL2VPNCircuitRecordFromOrganization = (circuitRecordId: string, organizationId: string) => {
    pendingRemove.current = { circuitRecordId: circuitRecordId, organizationId: organizationId };
    setIsRemoveL2VPNCircuitFromOrganizationDialogOpen(true);
  };

  // Called when user confirms in alert dialog
  const handleConfirmRemoveL2VPNCircuitFromOrganization = async () => {
    if (!pendingRemove) return;
    try {
      await removeL2VPNCircuitRecordFromOrganization.mutateAsync({
        circuitRecordId: pendingRemove.current.circuitRecordId,
        organizationId: pendingRemove.current.organizationId,
      });
      showSuccess(SUCCESS.SUBSCRIPTION.REMOVED_FROM_ACCOUNT);
      refetchAccountL2VPNCircuitsData();
    } catch (error) {
      showErrorWithApiError(error, ERROR.SUBSCRIPTION.REMOVE_FROM_ACCOUNT);
    } finally {
      setIsRemoveL2VPNCircuitFromOrganizationDialogOpen(false);
      pendingRemove.current = null;
    }
  };

  return (
    <ContainerLayout shouldShowSidebar={true} shouldAllowRegionChange={true} shouldAllowAccountChange={false}>
      <div className="flex flex-col flex-1 p-4 space-y-4">
        {/* Fixed Header Section */}
        <div className="p-4 flex items-center justify-between rounded-lg bg-muted">
          <div>
            <h1 className="text-3xl font-bold">L2VPN Circuits</h1>
            <p className="text-lg">Manage L2VPN circuits for <span className="font-bold text-blue-500">{selectedRegionCode}</span> datapool</p>
          </div>
          {canManage && (
            <Button
              variant="default"
              className="gap-2"
              onClick={() => setIsAddL2VPNCircuitToOrganizationDialogOpen(true)}
              disabled={isLoading}
            >
              <Plus className="h-4 w-4" />
              Add L2VPN Circuit to Organization
            </Button>
          )}
        </div>
        {/* Search Bar */}
        {accountL2VPNCircuits && accountL2VPNCircuits.length > 0 && (
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search L2VPN circuits by name, circuit ID, or account name..."
          />
        )}

        <Separator />

        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Loading L2VPN Circuits...</p>
          </div>
        ) : (
          error ? (
            <EmptyView
              icon={Network}
              title="Error Loading L2VPN Circuits"
              description="Error loading L2VPN circuits at the moment. Please try again."
              actionLabel="Retry"
              onAction={refetchL2VPNCircuits}
            />
          ) : (
            <>
              {/* Scrollable Content Section */}
              {filteredL2VPNCircuits && filteredL2VPNCircuits.length > 0 ? (
                <div className="flex-1 overflow-y-auto space-y-4">
                  {Object.entries(groupedL2VPNCircuits).map(([groupKey, l2vpnCircuits]) => {
                    const organizationL2vpnCircuit = filteredL2VPNCircuits?.find(organizationL2vpnCircuit => organizationL2vpnCircuit.organizationId === groupKey);
                    const organization = { organizationId: groupKey, name: organizationL2vpnCircuit?.name } as Organization;
                    return (
                      <Card key={organizationL2vpnCircuit?.organizationId} className="flex flex-col gap-4">
                        {/* Group Header */}
                        <CardHeader
                          className={`p-4 pb-0 ${organization ? 'cursor-pointer' : ''}`}
                          onClick={() => organization && onManageOrganization(organization)}>
                          <div className="flex justify-between">
                            <div className="flex flex-row items-center gap-2">
                              <div className="w-12 h-12 rounded-lg flex items-center justify-center border">
                                <Building2 className="h-6 w-6" />
                              </div>
                              <div>
                                <CardTitle className="text-xl font-semibold">{organizationL2vpnCircuit?.name}</CardTitle>
                              </div>
                            </div>
                            <div className="flex flex-row items-center gap-2">
                              <Badge variant="default">
                                {l2vpnCircuits.length} L2VPN circuit{l2vpnCircuits.length > 1 ? 's' : ''}
                              </Badge>
                              {organization && <ChevronRight className="h-8 w-8" />}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="gap-4 space-y-4 pb-4 px-4">
                          {l2vpnCircuits.map((l2vpnCircuit) => (
                            <L2VPNCircuitCard
                              key={l2vpnCircuit.circuitId}
                              l2vpnCircuit={l2vpnCircuit}
                              onRemoveL2VPNCircuitRecordFromOrganization={canManage ? (recordId: string) => handleRemoveL2VPNCircuitRecordFromOrganization(recordId, organizationL2vpnCircuit?.organizationId) : undefined}
                            />
                          ))}
                        </CardContent>
                      </Card>
                    );
                  })}

                  <div className="glass-panel flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between rounded-lg">
                    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                      <span className="text-md">
                        Loaded <span className="font-medium">{accountL2VPNCircuits.length}</span>
                        {' '}of <span className="font-bold">{totalCount}</span> accounts
                      </span>
                      {filteredL2VPNCircuits.length !== accountL2VPNCircuits.length && (
                        <span className="text-sm">
                          ({filteredL2VPNCircuits.length} match current search query)
                        </span>
                      )}
                    </div>
                    {hasNextPage ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-2 self-start sm:self-auto"
                        disabled={isFetchingNextPage}
                        onClick={() => void fetchNextPage()}
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading…
                          </>
                        ) : (
                          'Load more'
                        )}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <EmptyView
                  icon={Network}
                  title={searchTerm ? "No Matching L2VPN Circuits Found" : "No L2VPN Circuits Found"}
                  description={searchTerm ? "Try adjusting your search criteria to find what you're looking for." : "There are no L2VPN circuits available."}
                  actionLabel={searchTerm && hasNextPage ? "Load More" : undefined}
                  onAction={searchTerm && hasNextPage ? () => void fetchNextPage() : undefined}
                />
              )}
            </>
          ))}
      </div>
      {/* Add L2VPN Circuit to Organization Dialog */}
      <AddL2VPNCircuitToAccountDialog
        open={isAddL2VPNCircuitToOrganizationDialogOpen}
        onOpenChange={setIsAddL2VPNCircuitToOrganizationDialogOpen}
        onSuccess={() => refetchAccountL2VPNCircuitsData()}
        selectedRegionCode={selectedRegionCode}
      />

      {/* Generic Alert Dialog for confirming subscription removal */}
      <GenericAlertDialog
        open={isRemoveL2VPNCircuitFromOrganizationDialogOpen}
        onOpenChange={setIsRemoveL2VPNCircuitFromOrganizationDialogOpen}
        title="Remove L2VPN Circuit from Organization"
        description="Are you sure you want to remove this L2VPN circuit from the organization?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleConfirmRemoveL2VPNCircuitFromOrganization}
        loading={removeL2VPNCircuitRecordFromOrganization.isPending}
      />
    </ContainerLayout>
  );
};

export default L2VPNCircuitsPage;
